import { useState, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAppStore } from './lib/store';
import { useAuth } from './contexts/AuthContext';
import { Coffee, User } from 'lucide-react';
import { ReaderSettingsModal } from './components/public/ReaderSettingsModal';
import { LoginView } from './components/auth/LoginView';
import { ForgotPasswordView } from './components/auth/ForgotPasswordView';
import { ResetPasswordView } from './components/auth/ResetPasswordView';
import { FloatingNav } from './components/navigation/FloatingNav';
import { SettingsPanel } from './components/settings/SettingsPanel';

const ReadFeedView = lazy(() =>
    import('./components/public/ReadFeedView').then((m) => ({ default: m.ReadFeedView }))
);

const SimpleNookReader = lazy(() =>
    import('./components/public/SimpleNookReader').then((m) => ({ default: m.SimpleNookReader }))
);
const PatronageLedger = lazy(() =>
    import('./components/public/PatronageLedger').then((m) => ({ default: m.PatronageLedger }))
);
const UniversalProfile = lazy(() =>
    import('./components/public/UniversalProfile').then((m) => ({ default: m.UniversalProfile }))
);
const ReaderLibrary = lazy(() =>
    import('./components/public/ReaderLibrary').then((m) => ({ default: m.ReaderLibrary }))
);

const SupportPricingView = lazy(() =>
    import('./components/public/SupportPricingView').then((m) => ({ default: m.SupportPricingView }))
);

function RouteLoader() {
    const { themeProfile } = useAppStore();
    return (
        <div className="min-h-[60vh] flex items-center justify-center" style={{ color: themeProfile.text }}>
            <div className="animate-pulse font-literary text-xl opacity-70">Brewing the page...</div>
        </div>
    );
}

export function PublicApp() {
    const { themeProfile, showSettings, setShowSettings } = useAppStore();
    const location = useLocation();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const { user } = useAuth();

    const topLinks = [
        { to: '/read', label: 'Read & Feed' },
        { to: '/library', label: 'Library' },
        { to: '/support', label: 'Support' },
    ];

    return (
        <div
            className="min-h-screen flex flex-col font-serif"
            style={{
                backgroundColor: themeProfile.background,
                color: themeProfile.text
            }}
        >
            <header
                className="h-16 flex items-center justify-between px-4 md:px-6 border-b"
                style={{
                    backgroundColor: themeProfile.paperSurface,
                    borderColor: themeProfile.border
                }}
            >
                <div className="flex items-center gap-3 md:gap-5">
                    <Link to="/read" className="flex items-center gap-2 group">
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105"
                            style={{ backgroundColor: `${themeProfile.accent}15` }}
                        >
                            <Coffee className="w-5 h-5" style={{ color: themeProfile.accent }} />
                        </div>
                        <span className="font-literary text-xl font-semibold hidden md:inline">The Coffee House</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-3">
                        {topLinks.map((link) => {
                            const active = location.pathname === link.to;
                            return (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className="px-3 py-1.5 rounded-full text-sm font-medium"
                                    style={{
                                        backgroundColor: active ? `${themeProfile.accent}22` : 'transparent',
                                        color: active ? themeProfile.accent : themeProfile.text,
                                        opacity: active ? 1 : 0.75,
                                    }}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}
                    </nav>
                </div>

                <div className="flex gap-3 items-center">
                    {user ? (
                        <button
                            onClick={() => setIsSettingsOpen(true)}
                            className="w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                            style={{ backgroundColor: `${themeProfile.text}10` }}
                            title="Reader settings"
                        >
                            <User className="w-4 h-4" style={{ color: themeProfile.text }} />
                        </button>
                    ) : (
                        <Link
                            to="/login"
                            className="text-sm font-medium hover:underline opacity-90 flex items-center gap-1 px-4 py-2 rounded-full"
                            style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                        >
                            Log in
                        </Link>
                    )}
                </div>
            </header>

            <ReaderSettingsModal
                isOpen={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
            />

            <SettingsPanel
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
            />

            <main className="flex-1 overflow-y-auto w-full">
                <Suspense fallback={<RouteLoader />}>
                    <Routes>
                        <Route path="/" element={<ReadFeedView />} />
                        <Route path="/read" element={<ReadFeedView />} />
                        <Route path="/feed" element={<ReadFeedView />} />
                        <Route path="/coffee-house" element={<ReadFeedView />} />
                        <Route path="/support" element={<SupportPricingView />} />
                        <Route path="/u/:pseudonym" element={<UniversalProfile />} />
                        <Route path="/studio/:pseudonym" element={<UniversalProfile />} />
                        <Route path="/read/:workId" element={<SimpleNookReader />} />
                        <Route path="/ledger" element={<PatronageLedger />} />
                        <Route path="/library" element={<ReaderLibrary />} />
                        <Route path="/login" element={<LoginView />} />
                        <Route path="/forgot-password" element={<ForgotPasswordView />} />
                        <Route path="/reset-password" element={<ResetPasswordView />} />
                    </Routes>
                </Suspense>
            </main>

            {!location.pathname.startsWith('/read/') &&
                !['/login', '/forgot-password', '/reset-password'].includes(location.pathname) &&
                <FloatingNav />}
        </div>
    );
}
